import React, { useState, useEffect, useCallback } from 'react';
import { getConversations, type Conversation } from '../services/messages';
import ChatView from './ChatView';
import { RefreshIcon } from './icons/AdminIcons';
import '../pages/ChatsPage.css';

function getFirstName(name?: string | null): string {
  if (!name || !name.trim()) return '';
  const parts = name.trim().split(/\s+/);
  return parts[0] || '';
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours === 0) {
      const minutes = Math.floor(diff / (1000 * 60));
      return minutes <= 1 ? 'Ahora' : `Hace ${minutes} min`;
    }
    return `Hace ${hours}h`;
  } else if (days === 1) return 'Ayer';
  else if (days < 7) return `Hace ${days} días`;
  return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

interface MessagingSectionProps {
  isDarkMode?: boolean;
  /** Editor con permiso mensajería: enviar como soporte para que el usuario final no note cambio */
  sendAsSupport?: boolean;
}

const MessagingSection: React.FC<MessagingSectionProps> = ({ isDarkMode, sendAsSupport = false }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      setError(null);
      const response = await getConversations();
      setConversations(response.conversations || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar conversaciones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  return (
    <div className={`admin-messaging-wrap ${isDarkMode ? 'dark-theme' : ''}`}>
      {error && <div className="chats-error" style={{ margin: '12px 20px' }}>{error}</div>}
      <div className="chats-container">
        <div className={`chats-sidebar ${selectedUserId ? 'with-chat' : ''}`}>
          <div className="chats-header">
            <h2>Mensajes</h2>
            <button
              type="button"
              className="chats-back-button"
              onClick={() => { setLoading(true); loadConversations(); }}
              title="Actualizar"
            >
              <RefreshIcon width={16} height={16} /> Actualizar
            </button>
          </div>
          <div className="chats-list">
            {loading ? (
              <div className="chats-loading">Cargando...</div>
            ) : conversations.length === 0 ? (
              <div className="chats-empty">No hay conversaciones</div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.userId}
                  className={`chat-item ${selectedUserId === conv.userId ? 'active' : ''} ${(conv.unreadCount ?? 0) > 0 ? 'unread' : ''}`}
                  onClick={() => setSelectedUserId(conv.userId)}
                >
                  <div className="chat-item-header">
                    <span className="chat-item-email">
                      {conv.role === 'admin'
                        ? (conv.name && conv.name !== conv.email ? `${getFirstName(conv.name)} (Soporte)` : 'Soporte')
                        : (conv.name && conv.name !== conv.email ? getFirstName(conv.name) : conv.email)}
                    </span>
                    {(conv.unreadCount ?? 0) > 0 && (
                      <span className="chat-item-badge">{conv.unreadCount}</span>
                    )}
                  </div>
                  <div className="chat-item-preview">
                    <span className="chat-item-message">{conv.lastMessage || 'Sin mensajes'}</span>
                    <span className="chat-item-date">{formatDate(conv.lastMessageDate || new Date().toISOString())}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        {selectedUserId ? (
          <div className="chats-main">
            <ChatView
              otherUserId={selectedUserId}
              onBack={() => setSelectedUserId(null)}
              onMessageSent={loadConversations}
              currentUserRole="admin"
              hideBackButton={false}
              sendAsSupport={sendAsSupport}
            />
          </div>
        ) : (
          <div className="chats-empty-state">
            <p>Selecciona una conversación para comenzar</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagingSection;
